import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Space, Tag, Typography, Popconfirm, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { PurchaseOrder } from '../../types';

const { Title } = Typography;

const PurchaseOrderList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [searchText, setSearchText] = useState('');

  // 獲取採購訂單數據
  const fetchOrders = async () => {
    setLoading(true);
    try {
      const data = await api.purchaseOrders.getAll() as PurchaseOrder[];
      setOrders(data);
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
      message.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // 刪除採購訂單
  const handleDelete = async (id: string) => {
    try {
      await api.purchaseOrders.delete(id);
      message.success(t('common.success'));
      fetchOrders();
    } catch (error) {
      console.error('Error deleting purchase order:', error);
      message.error(t('common.error'));
    }
  };

  // 表格列定義
  const columns: ColumnsType<PurchaseOrder> = [
    {
      title: t('purchaseOrders.orderId'),
      dataIndex: 'order_id',
      key: 'order_id',
      sorter: (a: PurchaseOrder, b: PurchaseOrder) => a.order_id.localeCompare(b.order_id),
      filteredValue: searchText ? [searchText] : null,
      onFilter: (value: string | number | boolean, record: PurchaseOrder) => {
        const searchValue = String(value).toLowerCase();
        return record.order_id.toLowerCase().includes(searchValue) ||
          record.store_id.toLowerCase().includes(searchValue);
      },
    },
    {
      title: t('purchaseOrders.storeId'),
      dataIndex: 'store_id',
      key: 'store_id',
    },
    {
      title: t('purchaseOrders.platform'),
      dataIndex: 'platform',
      key: 'platform',
      filters: Array.from(new Set(orders.map(order => order.platform))).map(platform => ({
        text: platform,
        value: platform,
      })),
      onFilter: (value: string | number | boolean, record: PurchaseOrder) => record.platform === String(value),
    },
    {
      title: t('purchaseOrders.purchaseDate'),
      dataIndex: 'purchase_date',
      key: 'purchase_date',
      sorter: (a: PurchaseOrder, b: PurchaseOrder) => {
        const dateA = typeof a.purchase_date === 'string'
          ? new Date(a.purchase_date).getTime()
          : (a.purchase_date as any).valueOf();
        const dateB = typeof b.purchase_date === 'string'
          ? new Date(b.purchase_date).getTime()
          : (b.purchase_date as any).valueOf();
        return dateA - dateB;
      },
    },
    {
      title: t('purchaseOrders.totalAmount'),
      dataIndex: 'total_amount',
      key: 'total_amount',
      sorter: (a: PurchaseOrder, b: PurchaseOrder) => a.total_amount - b.total_amount,
    },
    {
      title: t('purchaseOrders.courierStatus'),
      dataIndex: 'courier_status',
      key: 'courier_status',
      render: (status: string) => (
        <Tag color={status === '已填寫' ? 'success' : 'warning'}>
          {status}
        </Tag>
      ),
      filters: [
        { text: t('purchaseOrders.courierStatuses.filled'), value: '已填寫' },
        { text: t('purchaseOrders.courierStatuses.unfilled'), value: '未填寫' },
      ],
      onFilter: (value: string | number | boolean, record: PurchaseOrder) => record.courier_status === String(value),
    },
    {
      title: t('common.action'),
      key: 'action',
      render: (_: any, record: PurchaseOrder) => (
        <Space size="middle">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/purchase-orders/${record.id}`)}
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => navigate(`/purchase-orders/${record.id}/edit`)}
          />
          <Popconfirm
            title={t('purchaseOrders.confirmDelete')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={2}>{t('purchaseOrders.title')}</Title>
        <Space>
          <Input
            placeholder={t('common.search')}
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 200 }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/purchase-orders/new')}
          >
            {t('purchaseOrders.addOrder')}
          </Button>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={orders}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />
    </div>
  );
};

export default PurchaseOrderList;
